import { useState, useEffect, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";

/**
 * Given an array of tasks, collects all target_blocks IDs from custom_products
 * and fetches their names and areas in a single query.
 * Returns a Map<blockId, blockName>.
 */
export function useBlockNames(tasks: Array<{ custom_products?: any }>) {
  const [blockNameMap, setBlockNameMap] = useState<Map<string, string>>(new Map());
  const [blockAreaMap, setBlockAreaMap] = useState<Map<string, number | null>>(new Map());

  // Collect all unique block IDs from target_blocks across all tasks
  const allBlockIds = useMemo(() => {
    const ids = new Set<string>();
    for (const task of tasks) {
      if (task.custom_products && Array.isArray(task.custom_products)) {
        const targetBlocks = task.custom_products[0]?.target_blocks;
        if (Array.isArray(targetBlocks)) {
          for (const id of targetBlocks) {
            if (typeof id === "string") ids.add(id);
          }
        }
      }
    }
    return Array.from(ids);
  }, [tasks]);

  useEffect(() => {
    if (allBlockIds.length === 0) {
      setBlockNameMap(new Map());
      setBlockAreaMap(new Map());
      return;
    }

    const fetchNames = async () => {
      const { data, error } = await supabase
        .from("blocks")
        .select("id, name, area_m2")
        .in("id", allBlockIds);

      if (error) {
        console.error("Error fetching block names:", error);
        return;
      }

      const nameMap = new Map<string, string>();
      const areaMap = new Map<string, number | null>();
      for (const block of data || []) {
        nameMap.set(block.id, block.name);
        areaMap.set(block.id, block.area_m2);
      }
      setBlockNameMap(nameMap);
      setBlockAreaMap(areaMap);
    };

    fetchNames();
  }, [allBlockIds]);

  return { blockNameMap, blockAreaMap };
}

/**
 * Extract target_blocks from a task's custom_products.
 */
export function getTargetBlocks(task: { custom_products?: any }): string[] {
  if (!task.custom_products || !Array.isArray(task.custom_products)) return [];
  const targetBlocks = task.custom_products[0]?.target_blocks;
  return Array.isArray(targetBlocks) ? targetBlocks : [];
}
